import React from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Route } from "react-router-dom";
import { useAuth } from "../../context/auth-context";
import ErrorFallback from "../errorHandling/error-fallback-page";
import { handleBoundaryError } from '../../utils/globalErrorHandler';
import RouteLoadingSpinner from "../loading/RouteLoadingSpinner";
import LoginRedirect from "./LoginRedirect";

/**
 * SuperAdminRoute - Route wrapper that restricts access to super-admins only.
 * Decodes the JWT access token to check the `is_super_admin` claim.
 * Redirects non-super-admin users to `/login`.
 *
 * @param {Object} props
 * @param {React.ComponentType} props.component - The component to render for super-admins.
 * @returns {JSX.Element}
 */
export default function SuperAdminRoute({ component: Component, ...rest }) {
  const { currentUser, accessToken } = useAuth();

  /**
   * Synchronously decodes the JWT access token to check for super-admin privileges.
   * @type {boolean}
   */
  const isSuperAdmin = (() => {
    if (!accessToken) return false;
    try {
      const payload = JSON.parse(atob(accessToken.split(".")[1]));
      return payload.is_super_admin === true;
    } catch {
      return false;
    }
  })();

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback} onError={handleBoundaryError}>
      <Route
        {...rest}
        render={(props) => {
          if (!currentUser && accessToken) return <RouteLoadingSpinner />;
          return currentUser && isSuperAdmin ? (
            <Component {...props} />
          ) : (
            <LoginRedirect />
          );
        }}
      />
    </ErrorBoundary>
  );
}
